/**
 * Plane geometry primitives.
 *
 * Everything works in the model's coordinate system, which is millimetres with
 * y pointing down -- the same orientation as the canvas, so nothing has to be
 * flipped when drawing. That choice makes "positive signed area" mean clockwise
 * on screen; the convention is stated wherever it matters rather than left for
 * the reader to rediscover.
 */

import type { Point } from './types';

/** Distances below this are treated as the same point (0.1 mm). */
export const EPSILON = 0.1;

export function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function subtract(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(p: Point, k: number): Point {
  return { x: p.x * k, y: p.y * k };
}

export function dot(a: Point, b: Point): number {
  return a.x * b.x + a.y * b.y;
}

/** Z component of the 3D cross product; its sign tells you which side b is on. */
export function cross(a: Point, b: Point): number {
  return a.x * b.y - a.y * b.x;
}

export function length(p: Point): number {
  return Math.hypot(p.x, p.y);
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function normalize(p: Point): Point {
  const len = length(p);
  return len < 1e-9 ? { x: 0, y: 0 } : { x: p.x / len, y: p.y / len };
}

/** Rotates 90 degrees. Paired with our orientation convention in inset(). */
export function perpendicular(p: Point): Point {
  return { x: -p.y, y: p.x };
}

export function angleOf(p: Point): number {
  return Math.atan2(p.y, p.x);
}

export function pointsEqual(a: Point, b: Point, epsilon = EPSILON): boolean {
  return Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon;
}

export function round(p: Point): Point {
  return { x: Math.round(p.x), y: Math.round(p.y) };
}

/**
 * Signed area by the shoelace formula. Positive means clockwise on screen,
 * given y points down.
 */
export function signedArea(polygon: Point[]): number {
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    sum += current.x * next.y - next.x * current.y;
  }
  return sum / 2;
}

export function area(polygon: Point[]): number {
  return Math.abs(signedArea(polygon));
}

/** Total length of a closed polygon's edges. */
export function perimeter(polygon: Point[]): number {
  let total = 0;
  for (let i = 0; i < polygon.length; i++) {
    total += distance(polygon[i], polygon[(i + 1) % polygon.length]);
  }
  return total;
}

/**
 * Closest point on segment ab to p, together with how far along the segment it
 * sits as a fraction in [0, 1]. Used for snapping and for placing an opening
 * where the pointer is.
 */
export function closestPointOnSegment(
  p: Point,
  a: Point,
  b: Point,
): { point: Point; t: number; distance: number } {
  const ab = subtract(b, a);
  const lengthSquared = dot(ab, ab);
  if (lengthSquared < 1e-9) {
    return { point: a, t: 0, distance: distance(p, a) };
  }
  const raw = dot(subtract(p, a), ab) / lengthSquared;
  const t = Math.max(0, Math.min(1, raw));
  const point = add(a, scale(ab, t));
  return { point, t, distance: distance(p, point) };
}

export function distanceToSegment(p: Point, a: Point, b: Point): number {
  return closestPointOnSegment(p, a, b).distance;
}

/**
 * Intersection of two infinite lines, each given as a point and a direction.
 * Returns null when they are parallel or near-parallel, which the caller must
 * handle rather than pretending a corner exists.
 */
export function lineIntersection(
  p1: Point,
  d1: Point,
  p2: Point,
  d2: Point,
): Point | null {
  const denominator = cross(d1, d2);
  if (Math.abs(denominator) < 1e-9) return null;
  const t = cross(subtract(p2, p1), d2) / denominator;
  return add(p1, scale(d1, t));
}

/**
 * Even-odd ray casting. Points exactly on an edge are not guaranteed either
 * way, which is fine for its purpose: locating an interior seed point.
 */
export function pointInPolygon(p: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const straddles = a.y > p.y !== b.y > p.y;
    if (!straddles) continue;
    const crossingX = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (p.x < crossingX) inside = !inside;
  }
  return inside;
}

/** Arithmetic mean of the vertices. Cheap, and good enough for labelling. */
export function centroid(polygon: Point[]): Point {
  if (polygon.length === 0) return { x: 0, y: 0 };
  const sum = polygon.reduce((acc, p) => add(acc, p), { x: 0, y: 0 });
  return scale(sum, 1 / polygon.length);
}

/**
 * A point guaranteed to be inside the polygon, used as a room's seed.
 *
 * The centroid is inside for convex shapes but can fall outside an L-shaped
 * room, which is exactly the shape renovation produces, so fall back to
 * scanning horizontal chords for the widest interior span.
 */
export function interiorPoint(polygon: Point[]): Point {
  const middle = centroid(polygon);
  if (pointInPolygon(middle, polygon)) return middle;

  const ys = polygon.map((p) => p.y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  let best: { point: Point; width: number } | null = null;
  const samples = 32;
  for (let i = 1; i < samples; i++) {
    const y = minY + ((maxY - minY) * i) / samples;
    const crossings: number[] = [];
    for (let j = 0, k = polygon.length - 1; j < polygon.length; k = j++) {
      const a = polygon[j];
      const b = polygon[k];
      if (a.y > y === b.y > y) continue;
      crossings.push(((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x);
    }
    crossings.sort((m, n) => m - n);
    for (let c = 0; c + 1 < crossings.length; c += 2) {
      const width = crossings[c + 1] - crossings[c];
      if (!best || width > best.width) {
        best = { point: { x: (crossings[c] + crossings[c + 1]) / 2, y }, width };
      }
    }
  }
  return best ? best.point : middle;
}

/**
 * Moves every edge of a polygon inward by its own distance and rebuilds the
 * corners, giving the space enclosed by the inner wall faces rather than by
 * their centrelines.
 *
 * This is what makes computed areas comparable with the m² printed on a listing
 * plan: centreline area overstates a room by roughly perimeter x thickness / 2,
 * which on a small bathroom is the difference between 4.1 and 3.6 m².
 *
 * `insets[i]` applies to the edge from polygon[i] to polygon[i + 1]. The polygon
 * must be clockwise on screen (positive signed area).
 */
export function insetPolygon(polygon: Point[], insets: number[]): Point[] {
  if (polygon.length < 3) return polygon;

  // For a clockwise-on-screen polygon the interior lies to the left of each
  // edge direction, which perpendicular() gives directly.
  const lines = polygon.map((current, i) => {
    const next = polygon[(i + 1) % polygon.length];
    const direction = normalize(subtract(next, current));
    const inward = perpendicular(direction);
    const offset = scale(inward, insets[i] ?? 0);
    return { point: add(current, offset), direction };
  });

  const result: Point[] = [];
  for (let i = 0; i < lines.length; i++) {
    const previous = lines[(i - 1 + lines.length) % lines.length];
    const current = lines[i];
    const corner = lineIntersection(
      previous.point,
      previous.direction,
      current.point,
      current.direction,
    );
    // Parallel neighbours mean a straight run through a vertex; the offset
    // point on the current edge is then the right answer.
    result.push(corner ?? current.point);
  }
  return result;
}

/** Square millimetres to square metres. */
export function mm2ToM2(squareMillimetres: number): number {
  return squareMillimetres / 1_000_000;
}

/** Millimetres to metres. */
export function mmToM(millimetres: number): number {
  return millimetres / 1000;
}
